/**
 * Forecast Tab (Pattern of Life)
 * ------------------------------
 * Forward-looking "Expected Day" view for the selected entity.
 *
 * Renders the baseline-synthesized forecast served by the Phase 1 backend at
 * POST /api/pattern-of-life/predict/expected-day. Phase 2 adds a Forecast
 * Quality summary (rolling backtest accuracy), Phase 3 will add an LSTM
 * surprise score row, and Phase 4 will add a live "Right Now" projection.
 *
 * The visual language matches the Dwells and Digital Fingerprints tabs:
 *   - Compact KPI row (label above value, tiny font, evenly spread)
 *   - Outlined chip vocabulary (h:16-18, fontSize:0.55-0.6rem)
 *   - Sparing use of color — activity-kind tints used only on the timeline
 *     and as a 2px left rule on event rows; no full-row colored backgrounds.
 *   - No oversized icons in headers; 14-16px max.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  CircularProgress,
  Chip,
  Stack,
  Alert,
  Button,
  TextField,
  Divider,
} from '@mui/material';
import {
  Place as PlaceIcon,
  Route as RouteIcon,
  Groups as GroupsIcon,
  PhoneAndroid as DigitalIcon,
  Info as InfoIcon,
  Slideshow as SlideshowIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';
import polService, {
  ExpectedDayResponse,
  ExpectedDaySlot,
  ForecastQualityResponse,
  RightNowResponse,
} from '../../services/polService';
import { exportDayAheadBriefToPowerPoint } from '@signals/app-shell/utils/powerpointExport';

interface ForecastTabProps {
  selectedEntityId: string | null;
  courseId: string | null;
  /** Optional friendly name for the course; renders on the export brief cover. */
  courseName?: string;
  /** Called when user clicks an hour slot; map / timeline can re-focus. */
  onSlotClick?: (slot: ExpectedDaySlot) => void;
}

const HOURS = Array.from({ length: 24 }, (_, h) => h);

const ACTIVITY_KIND_COLOR: Record<string, string> = {
  visit: '#4CAF50',
  transit: '#2196F3',
  digital: '#9C27B0',
  copresence: '#FF9800',
  mixed: '#673AB7',
  idle: 'rgba(255,255,255,0.06)',
};

function tomorrowIsoDate(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function formatPercent(value: number): string {
  return `${Math.round((value || 0) * 100)}%`;
}

function confidenceBand(c: number): { label: string; color: string } {
  if (c >= 0.7) return { label: 'High', color: '#4CAF50' };
  if (c >= 0.4) return { label: 'Moderate', color: '#FFC107' };
  if (c >= 0.2) return { label: 'Low', color: '#FF7043' };
  return { label: 'Very low', color: '#9E9E9E' };
}

function maturityBand(m: number): { label: string; color: string } {
  if (m >= 0.8) return { label: 'Mature', color: '#4CAF50' };
  if (m >= 0.5) return { label: 'Developing', color: '#FFC107' };
  if (m >= 0.25) return { label: 'Sparse', color: '#FF7043' };
  return { label: 'New', color: '#9E9E9E' };
}

const ForecastTab: React.FC<ForecastTabProps> = ({
  selectedEntityId,
  courseId,
  courseName,
  onSlotClick,
}) => {
  const [targetDate, setTargetDate] = useState<string>(tomorrowIsoDate());
  const [forecast, setForecast] = useState<ExpectedDayResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [briefExporting, setBriefExporting] = useState(false);
  const [exportSnack, setExportSnack] = useState<string | null>(null);
  const [quality, setQuality] = useState<ForecastQualityResponse | null>(null);
  const [rightNow, setRightNow] = useState<RightNowResponse | null>(null);

  const loadForecast = useCallback(
    async (refresh = false) => {
      if (!selectedEntityId) {
        setForecast(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const resp = await polService.getExpectedDay({
          entity_id: selectedEntityId,
          target_date: targetDate,
          course_id: courseId || undefined,
          refresh,
        });
        setForecast(resp);
      } catch (err: any) {
        const detail =
          err?.response?.data?.detail || err?.message || 'Failed to load forecast';
        setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
        setForecast(null);
      } finally {
        setLoading(false);
      }
    },
    [selectedEntityId, targetDate, courseId],
  );

  useEffect(() => {
    loadForecast();
  }, [loadForecast]);

  // Forecast Quality (Phase 2): lazy-load when an entity is selected.
  // Independent of date — quality reflects historical accuracy, not the
  // current target. We swallow the error silently so the rest of the tab
  // still renders if the backtests table does not exist yet.
  useEffect(() => {
    let cancelled = false;
    setQuality(null);
    if (!selectedEntityId) return;
    polService
      .getForecastQuality({ entity_id: selectedEntityId, course_id: courseId || undefined })
      .then((q) => {
        if (!cancelled) setQuality(q);
      })
      .catch(() => {
        if (!cancelled) setQuality(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedEntityId, courseId]);

  // Right Now (Phase 4 lightweight): poll every 60s while the tab is open and
  // an entity is selected. We never throw on failure — the panel just hides.
  useEffect(() => {
    let cancelled = false;
    setRightNow(null);
    if (!selectedEntityId) return;

    const fetchRightNow = () => {
      polService
        .getRightNow({ entity_id: selectedEntityId, course_id: courseId || undefined, top_n: 3 })
        .then((r) => {
          if (!cancelled) setRightNow(r);
        })
        .catch(() => {
          if (!cancelled) setRightNow(null);
        });
    };

    fetchRightNow();
    const interval = window.setInterval(fetchRightNow, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [selectedEntityId, courseId]);

  const handleExportBrief = useCallback(async () => {
    if (!forecast || !selectedEntityId) return;
    setBriefExporting(true);
    setExportSnack('Building Day Ahead brief...');
    try {
      const prompt =
        `You are writing a forward-looking Day Ahead brief for entity ${selectedEntityId} on ${forecast.day_of_week} ${forecast.forecast_date}. ` +
        `The system synthesized this forecast from established baselines (recurring patterns, route patterns, correlations, digital patterns, mobility baseline). ` +
        `Prediction confidence is ${(forecast.prediction_confidence * 100).toFixed(0)}% with ${(forecast.data_maturity * 100).toFixed(0)}% baseline maturity. ` +
        `Predicted: ${forecast.predicted_active_hours} active hours, ${forecast.predicted_total_dwells} stops at ${forecast.predicted_places_count} unique places, ~${forecast.predicted_distance_km.toFixed(1)}km travel. ` +
        `Write 600-900 words covering: (1) the high-confidence expectations for the day (locations, time windows, transits), ` +
        `(2) likely co-presence with other entities and what those windows imply, ` +
        `(3) digital activity to expect and any signal about device use, ` +
        `(4) caveats: where the forecast is least certain and why, given data maturity, ` +
        `(5) 3-5 concrete collection or validation actions an analyst should consider before/during the target day. ` +
        `Use plain paragraphs. No markdown, no headings, no bullet symbols — the export builder will format it.`;

      let aiNarrative = '';
      try {
        const aiResp = await fetch('/api/pol-chat/ask', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
          },
          body: JSON.stringify({
            question: prompt,
            context: { forecast, entity_id: selectedEntityId, course_id: courseId },
          }),
        });
        if (aiResp.ok) {
          const aiData = await aiResp.json();
          aiNarrative = aiData.response || '';
        }
      } catch (aiErr) {
        // AI is optional; the export still renders without a narrative.
        console.warn('Day Ahead brief AI narrative unavailable:', aiErr);
      }

      await exportDayAheadBriefToPowerPoint({
        entityId: selectedEntityId,
        courseName,
        forecastDate: forecast.forecast_date,
        dayOfWeek: forecast.day_of_week,
        predictionConfidence: forecast.prediction_confidence,
        dataMaturity: forecast.data_maturity,
        predictedActiveHours: forecast.predicted_active_hours,
        predictedTotalDwells: forecast.predicted_total_dwells,
        predictedDistanceKm: forecast.predicted_distance_km,
        predictedPlacesCount: forecast.predicted_places_count,
        slots: forecast.slots.map((s) => ({
          hour: s.hour,
          presence_probability: s.presence_probability,
          primary_activity: s.primary_activity,
          activity_kind: s.activity_kind,
          confidence: s.confidence,
        })),
        // For the exported brief we surface the unified `dow_likelihood`
        // as the primary "match" value but also keep `confidence` for
        // anyone consuming the legacy field.
        expectedVisits: forecast.expected_visits.map((v) => ({
          pattern_id: v.pattern_id,
          location_id: v.location_id,
          location_name: v.location_name,
          start_time: v.start_time,
          end_time: v.end_time,
          frequency: v.frequency,
          dow_likelihood: v.dow_likelihood,
          dow_sample_size: v.dow_sample_size,
          confidence: v.confidence,
          occurrence_count: v.occurrence_count,
          avg_duration_minutes: v.avg_duration_minutes,
          avg_arrival_variance_minutes: v.avg_arrival_variance_minutes,
        })),
        expectedTransits: forecast.expected_transits.map((t) => ({
          route_id: t.route_id,
          route_name: t.route_name,
          origin_name: t.origin_name,
          destination_name: t.destination_name,
          peak_hour: t.peak_hour,
          avg_duration_minutes: t.avg_duration_minutes,
          avg_distance_km: t.avg_distance_km,
          consistency: t.consistency,
          dow_likelihood: t.dow_likelihood,
          dow_trip_count: t.dow_trip_count,
          confidence: t.confidence,
          usage_count: t.usage_count,
          behavioral_tags: t.behavioral_tags,
        })),
        expectedCoPresence: forecast.expected_copresence.map((c) => ({
          other_entity_id: c.other_entity_id,
          correlation_type: c.correlation_type,
          correlation_strength: c.correlation_strength,
          dow_likelihood: c.dow_likelihood,
          dow_sample_size: c.dow_sample_size,
          confidence: c.confidence,
          active_hours: c.active_hours,
          avg_overlap_minutes: c.avg_overlap_minutes,
        })),
        expectedDigital: forecast.expected_digital.map((d) => ({
          pattern_category: d.pattern_category,
          pattern_name: d.pattern_name,
          pattern_description: d.pattern_description,
          peak_hour: d.peak_hour,
          peak_day: d.peak_day,
          dow_likelihood: d.dow_likelihood,
          confidence: d.confidence,
          usage_frequency: d.usage_frequency,
          days_active: d.days_active,
        })),
        sourcesUsed: forecast.sources_used,
        notes: forecast.notes,
        aiNarrative,
      });
      setExportSnack('Day Ahead brief exported');
    } catch (exc: any) {
      setExportSnack(`Export failed: ${exc?.message || 'unknown error'}`);
    } finally {
      setBriefExporting(false);
      setTimeout(() => setExportSnack(null), 3500);
    }
  }, [forecast, selectedEntityId, courseId, courseName]);

  const conf = useMemo(
    () => confidenceBand(forecast?.prediction_confidence ?? 0),
    [forecast?.prediction_confidence],
  );
  const mat = useMemo(
    () => maturityBand(forecast?.data_maturity ?? 0),
    [forecast?.data_maturity],
  );

  if (!selectedEntityId) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <InsightsIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
        <Typography variant="subtitle2" color="text.primary" sx={{ fontSize: '0.85rem' }}>
          Select an entity to generate a forecast
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ maxWidth: 460, mx: 'auto', mt: 0.5, display: 'block' }}
        >
          The Forecast tab projects an entity&apos;s expected day by blending recurring
          patterns, route patterns, co-presence and digital baselines into a forward
          24-hour timeline. Pick any future date to see what the system expects, or a
          past date to compare against actuals.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Header row */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'center' }}
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.9rem' }}
          >
            <InsightsIcon sx={{ fontSize: 16 }} />
            Expected Day &middot; {selectedEntityId}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
            Updates automatically when entity or date changes
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
          <TextField
            type="date"
            size="small"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 150,
              '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.5 },
            }}
            disabled={loading}
          />
          {loading && <CircularProgress size={14} />}
          <Tooltip
            title={
              !forecast
                ? 'Generate a forecast first'
                : 'Export Day Ahead brief (PowerPoint with AI narrative)'
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  briefExporting ? (
                    <CircularProgress size={12} />
                  ) : (
                    <SlideshowIcon sx={{ fontSize: 14 }} />
                  )
                }
                disabled={!forecast || briefExporting}
                onClick={handleExportBrief}
                sx={{
                  whiteSpace: 'nowrap',
                  fontSize: '0.65rem',
                  py: 0.5,
                  px: 1,
                  minWidth: 0,
                }}
              >
                {briefExporting ? 'Building' : 'Export'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Status chip row */}
      {forecast && (
        <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
          <Chip
            size="small"
            label={`${forecast.day_of_week} · ${forecast.forecast_date}`}
            variant="outlined"
            sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.6 } }}
          />
          <Chip
            size="small"
            label={`Confidence ${conf.label} · ${formatPercent(forecast.prediction_confidence)}`}
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 600,
              bgcolor: 'transparent',
              border: '1px solid',
              borderColor: conf.color,
              color: conf.color,
              '& .MuiChip-label': { px: 0.6 },
            }}
          />
          <Chip
            size="small"
            label={`Baseline ${mat.label} · ${formatPercent(forecast.data_maturity)}`}
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 600,
              bgcolor: 'transparent',
              border: '1px solid',
              borderColor: mat.color,
              color: mat.color,
              '& .MuiChip-label': { px: 0.6 },
            }}
          />
          <Chip
            size="small"
            label={forecast.is_in_future ? 'Forward' : 'Backtest mode'}
            variant="outlined"
            color={forecast.is_in_future ? 'default' : 'primary'}
            sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.6 } }}
          />
        </Stack>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ py: 0.5, mb: 1 }}>
          <Typography variant="caption">{error}</Typography>
        </Alert>
      )}

      {loading && !forecast && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <CircularProgress size={22} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Synthesizing expected day from baselines…
          </Typography>
        </Box>
      )}

      {exportSnack && (
        <Alert
          severity={exportSnack.startsWith('Export failed') ? 'error' : 'info'}
          onClose={() => setExportSnack(null)}
          sx={{ py: 0.5, mb: 1 }}
        >
          <Typography variant="caption">{exportSnack}</Typography>
        </Alert>
      )}

      {/* Right Now panel — Phase 4 lightweight. Shown above the forecast
          when present (poll-driven, 60s refresh) so the tab leads with the
          live picture rather than tomorrow's prediction. */}
      {rightNow && <RightNowPanel snapshot={rightNow} />}

      {forecast && (
        <>
          {/* KPI strip — Dwells-style, evenly spread, compact */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: quality ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)',
              gap: 0.5,
              mb: 1,
            }}
          >
            <KpiCell
              label="Active"
              value={`${forecast.predicted_active_hours}h`}
              caption={`${HOURS.length - forecast.predicted_active_hours}h idle`}
            />
            <KpiCell
              label="Stops"
              value={String(forecast.predicted_total_dwells)}
              caption={`${forecast.predicted_places_count} places`}
            />
            <KpiCell
              label="Distance"
              value={`${forecast.predicted_distance_km.toFixed(1)}km`}
              caption={`med ${forecast.expected_mobility.median_distance_km.toFixed(1)}km`}
            />
            <KpiCell
              label="Co-Pres"
              value={String(forecast.expected_copresence.length)}
              caption="partners"
            />
            {quality && (
              <KpiCell
                label="Quality"
                value={
                  quality.rolling_accuracy != null
                    ? formatPercent(quality.rolling_accuracy)
                    : '—'
                }
                caption={
                  quality.backtests_run > 0
                    ? `${quality.backtests_run} backtests`
                    : 'no backtests'
                }
                color={
                  quality.rolling_accuracy == null
                    ? undefined
                    : quality.rolling_accuracy >= 0.6
                      ? '#4CAF50'
                      : quality.rolling_accuracy >= 0.35
                        ? '#FFC107'
                        : '#FF7043'
                }
              />
            )}
          </Box>

          {/* 24h Timeline */}
          <Box
            sx={{
              mb: 1,
              p: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
              <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                24-Hour Forecast Timeline
              </Typography>
              <Tooltip title="Each cell shows the dominant activity kind and confidence for that hour. Hover for the patterns that contributed.">
                <InfoIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
              </Tooltip>
            </Stack>
            <ForecastTimeline slots={forecast.slots} onSlotClick={onSlotClick} />
            <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
              {(['visit', 'transit', 'digital', 'copresence', 'mixed', 'idle'] as const).map((k) => (
                <Stack key={k} direction="row" alignItems="center" spacing={0.3}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: 0.25,
                      bgcolor: ACTIVITY_KIND_COLOR[k],
                      opacity: k === 'idle' ? 0.4 : 0.85,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ textTransform: 'capitalize', fontSize: '0.55rem' }}
                  >
                    {k}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          {/* Visits + Transits */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 1,
              mb: 1,
            }}
          >
            <SectionCard
              icon={<PlaceIcon sx={{ fontSize: 14, color: ACTIVITY_KIND_COLOR.visit }} />}
              title="Expected Visits"
              count={forecast.expected_visits.length}
              emptyText="No recurring visits match this day-of-week."
            >
              {forecast.expected_visits.slice(0, 8).map((v, idx) => {
                // Use the unified per-DOW likelihood (= adherence_rate when
                // populated). Fall back to legacy confidence so older cached
                // payloads still render until the next pipeline pass.
                const matchValue =
                  v.dow_likelihood != null && v.dow_likelihood > 0
                    ? v.dow_likelihood
                    : v.confidence;
                const sampleHint =
                  v.dow_sample_size > 0
                    ? `${v.dow_sample_size} prior`
                    : v.occurrence_count > 0
                    ? `${v.occurrence_count} prior`
                    : null;
                return (
                  <EventRow
                    key={`${v.pattern_id ?? 'v'}_${idx}`}
                    accentColor={ACTIVITY_KIND_COLOR.visit}
                    title={v.location_name || v.location_id || 'Unnamed location'}
                    confidence={matchValue}
                    detail={[
                      `${v.start_time}–${v.end_time}`,
                      v.frequency,
                      sampleHint,
                      v.avg_duration_minutes
                        ? `~${Math.round(v.avg_duration_minutes)}min`
                        : null,
                      v.avg_arrival_variance_minutes
                        ? `±${Math.round(v.avg_arrival_variance_minutes)}min`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                );
              })}
            </SectionCard>

            <SectionCard
              icon={<RouteIcon sx={{ fontSize: 14, color: ACTIVITY_KIND_COLOR.transit }} />}
              title="Expected Transits"
              count={forecast.expected_transits.length}
              emptyText="No route patterns match this day-of-week."
            >
              {forecast.expected_transits.slice(0, 8).map((t, idx) => {
                // Same unified treatment as Visits: dow_likelihood is the
                // honest "fraction of past trips on this DOW" rate, falling
                // back to the table-level confidence for legacy payloads.
                const matchValue =
                  t.dow_likelihood != null && t.dow_likelihood > 0
                    ? t.dow_likelihood
                    : t.confidence;
                const dowAbbr = forecast.day_of_week.slice(0, 3);
                const sampleHint =
                  t.dow_trip_count > 0 && t.usage_count > 0
                    ? `${t.dow_trip_count}/${t.usage_count} ${dowAbbr}s`
                    : t.usage_count > 0
                    ? `${t.usage_count} trips`
                    : null;
                return (
                  <EventRow
                    key={`${t.route_id ?? 't'}_${idx}`}
                    accentColor={ACTIVITY_KIND_COLOR.transit}
                    title={
                      t.route_name ||
                      `${t.origin_name ?? '?'} → ${t.destination_name ?? '?'}`
                    }
                    confidence={matchValue}
                    detail={[
                      t.peak_hour != null
                        ? `Peak ${String(t.peak_hour).padStart(2, '0')}:00`
                        : null,
                      sampleHint,
                      t.avg_duration_minutes
                        ? `~${Math.round(t.avg_duration_minutes)}min`
                        : null,
                      t.avg_distance_km ? `${t.avg_distance_km.toFixed(1)}km` : null,
                      t.behavioral_tags?.length
                        ? t.behavioral_tags.slice(0, 2).join(', ')
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                );
              })}
            </SectionCard>
          </Box>

          {/* Co-presence + Digital */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 1,
              mb: 1,
            }}
          >
            <SectionCard
              icon={<GroupsIcon sx={{ fontSize: 14, color: ACTIVITY_KIND_COLOR.copresence }} />}
              title="Expected Co-Presence"
              count={forecast.expected_copresence.length}
              emptyText="No active correlations align with this day-of-week."
            >
              {forecast.expected_copresence.slice(0, 6).map((c, idx) => {
                // Prefer the per-DOW observed likelihood (honest "how often
                // are they together on this day-of-week") over the raw
                // correlation confidence. Fall back if the field is missing
                // from older API responses.
                const hasDowSignal = c.dow_sample_size >= 2;
                const matchValue = hasDowSignal ? c.dow_likelihood : c.confidence;
                const sampleHint = hasDowSignal
                  ? `${Math.round(c.dow_likelihood * c.dow_sample_size)}/${c.dow_sample_size} ${
                      forecast.day_of_week.slice(0, 3)
                    }s`
                  : 'no DOW samples';
                return (
                  <EventRow
                    key={`${c.other_entity_id}_${idx}`}
                    accentColor={ACTIVITY_KIND_COLOR.copresence}
                    title={c.other_entity_id}
                    confidence={matchValue}
                    detail={[
                      c.correlation_type.replace(/_/g, ' '),
                      sampleHint,
                      c.active_hours.length
                        ? `hrs ${c.active_hours
                            .slice(0, 4)
                            .map((h) => String(h).padStart(2, '0'))
                            .join(',')}${c.active_hours.length > 4 ? '…' : ''}`
                        : null,
                      c.avg_overlap_minutes
                        ? `~${Math.round(c.avg_overlap_minutes)}min`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                );
              })}
            </SectionCard>

            <SectionCard
              icon={<DigitalIcon sx={{ fontSize: 14, color: ACTIVITY_KIND_COLOR.digital }} />}
              title="Expected Digital Activity"
              count={forecast.expected_digital.length}
              emptyText="No high-confidence digital patterns established yet."
            >
              {forecast.expected_digital.slice(0, 6).map((d, idx) => {
                // Unified pill: dow_likelihood = confidence when the
                // pattern's peak day matches today, a heavily penalized
                // value when it doesn't, or usage_frequency for DOW-agnostic
                // patterns. Fall back to confidence for legacy payloads.
                const matchValue =
                  d.dow_likelihood != null && d.dow_likelihood > 0
                    ? d.dow_likelihood
                    : d.confidence;
                const dowAbbr = forecast.day_of_week.slice(0, 3);
                const dowHint = d.peak_day
                  ? d.peak_day === forecast.day_of_week
                    ? `peak ${dowAbbr}`
                    : `peak ${d.peak_day.slice(0, 3)} (off-day)`
                  : d.usage_frequency != null
                  ? `${Math.round(d.usage_frequency * 100)}% of days`
                  : null;
                return (
                  <EventRow
                    key={`${d.pattern_name}_${idx}`}
                    accentColor={ACTIVITY_KIND_COLOR.digital}
                    title={d.pattern_name}
                    confidence={matchValue}
                    detail={[
                      d.pattern_category,
                      d.peak_hour != null
                        ? `peak ${String(d.peak_hour).padStart(2, '0')}:00`
                        : null,
                      dowHint,
                      d.days_active ? `${d.days_active}d active` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    description={d.pattern_description}
                  />
                );
              })}
            </SectionCard>
          </Box>

          {/* Notes + sources */}
          {(forecast.notes.length > 0 || Object.keys(forecast.sources_used).length > 0) && (
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                mb: 1,
              }}
            >
              <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                Forecast Notes
              </Typography>
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {forecast.notes.map((n, i) => (
                  <Typography
                    key={i}
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.6rem' }}
                  >
                    • {n}
                  </Typography>
                ))}
              </Stack>
              {Object.keys(forecast.sources_used).length > 0 && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ fontSize: '0.55rem' }}
                  >
                    Sources:{' '}
                    {Object.entries(forecast.sources_used)
                      .map(([k, v]) => `${k.replace(/_/g, ' ')}=${v}`)
                      .join(' · ')}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ForecastTab;

// ---------------------------------------------------------------------------
// Internal subcomponents — kept compact to match Dwells/Fingerprints density
// ---------------------------------------------------------------------------

interface KpiCellProps {
  label: string;
  value: string;
  caption?: string;
  /** Optional color override (used by the Quality cell). */
  color?: string;
}

const KpiCell: React.FC<KpiCellProps> = ({ label, value, caption, color }) => (
  <Box
    sx={{
      py: 0.5,
      borderRadius: 0.5,
      bgcolor: 'rgba(255,255,255,0.04)',
      border: '1px solid',
      borderColor: 'divider',
      textAlign: 'center',
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        fontSize: '0.5rem',
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        display: 'block',
        lineHeight: 1,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={700}
      sx={{ fontSize: '0.75rem', lineHeight: 1.3, color: color ?? 'text.primary' }}
    >
      {value}
    </Typography>
    {caption && (
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ fontSize: '0.5rem', display: 'block', lineHeight: 1 }}
      >
        {caption}
      </Typography>
    )}
  </Box>
);

interface SectionCardProps {
  icon: React.ReactNode;
  title: string;
  count: number;
  emptyText: string;
  children: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({ icon, title, count, emptyText, children }) => (
  <Box
    sx={{
      p: 1,
      borderRadius: 1,
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
      {icon}
      <Typography
        variant="caption"
        fontWeight="bold"
        sx={{ fontSize: '0.7rem', flex: 1 }}
      >
        {title}
      </Typography>
      <Chip
        size="small"
        label={count}
        variant="outlined"
        sx={{ height: 16, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 } }}
      />
    </Stack>
    {count === 0 ? (
      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', fontStyle: 'italic' }}>
        {emptyText}
      </Typography>
    ) : (
      <Stack spacing={0.5}>{children}</Stack>
    )}
  </Box>
);

interface EventRowProps {
  accentColor: string;
  title: string;
  confidence: number;
  detail: string;
  description?: string | null;
}

const EventRow: React.FC<EventRowProps> = ({ accentColor, title, confidence, detail, description }) => (
  <Box
    sx={{
      p: 0.75,
      pl: 1,
      borderRadius: 0.5,
      borderLeft: 2,
      borderLeftColor: accentColor,
      bgcolor: 'rgba(255,255,255,0.02)',
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.7rem',
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={title}
      >
        {title}
      </Typography>
      <Chip
        size="small"
        label={formatPercent(confidence)}
        variant="outlined"
        sx={{ height: 14, fontSize: '0.55rem', flexShrink: 0, '& .MuiChip-label': { px: 0.4 } }}
      />
    </Stack>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontSize: '0.6rem', display: 'block', mt: 0.25 }}
    >
      {detail}
    </Typography>
    {description && (
      <Typography
        variant="caption"
        sx={{ fontSize: '0.6rem', display: 'block', mt: 0.25, opacity: 0.75 }}
      >
        {description}
      </Typography>
    )}
  </Box>
);

// ---------------------------------------------------------------------------
// Right Now panel — Phase 4 (lightweight)
// ---------------------------------------------------------------------------

interface RightNowPanelProps {
  snapshot: RightNowResponse;
}

function formatAge(minutes: number | null): string {
  if (minutes == null) return '—';
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${Math.round(minutes)}m ago`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(1)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function surpriseColor(s: number | null): string {
  if (s == null) return '#9E9E9E';
  if (s <= 0.15) return '#4CAF50';
  if (s <= 0.4) return '#FFC107';
  return '#FF7043';
}

const RightNowPanel: React.FC<RightNowPanelProps> = ({ snapshot }) => {
  const { current_position: pos, expected_now, likely_next_stops, surprise_signal } = snapshot;
  const stale = pos.age_minutes != null && pos.age_minutes > 90;
  return (
    <Box
      sx={{
        p: 1,
        mb: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'rgba(255,255,255,0.02)',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ mb: 0.75 }}
      >
        <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
          Right Now
        </Typography>
        <Chip
          size="small"
          label={pos.source === 'unknown' ? 'no recent fix' : `${pos.source} · ${formatAge(pos.age_minutes)}`}
          variant="outlined"
          color={stale ? 'warning' : 'default'}
          sx={{ height: 16, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 } }}
        />
        {surprise_signal != null && (
          <Chip
            size="small"
            label={`Match ${Math.round((1 - surprise_signal) * 100)}%`}
            sx={{
              height: 16,
              fontSize: '0.55rem',
              fontWeight: 600,
              bgcolor: 'transparent',
              border: '1px solid',
              borderColor: surpriseColor(surprise_signal),
              color: surpriseColor(surprise_signal),
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        )}
        {snapshot.note && (
          <Tooltip title={snapshot.note}>
            <InfoIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
          </Tooltip>
        )}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 1,
        }}
      >
        {/* Current location */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: 0.3 }}>
            Current Location
          </Typography>
          {pos.source === 'unknown' ? (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', display: 'block' }}>
              No recent position
            </Typography>
          ) : (
            <>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, display: 'block' }}>
                {pos.location_name || pos.cluster || `${pos.lat?.toFixed(4)}, ${pos.lon?.toFixed(4)}`}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                {(() => {
                  // Prefer the backend's honest motion_state; fall back to
                  // the old is_currently_dwelling bool for older API payloads.
                  const ms = pos.motion_state;
                  if (ms === 'dwelling' || (ms == null && pos.is_currently_dwelling)) {
                    return 'dwelling';
                  }
                  if (ms === 'stationary') {
                    return pos.location_name
                      ? `stationary near ${pos.location_name}`
                      : 'stationary';
                  }
                  if (ms === 'in_transit') {
                    return 'in transit';
                  }
                  return 'last known';
                })()}
                {pos.lat != null && pos.lon != null
                  ? ` · ${pos.lat.toFixed(3)}, ${pos.lon.toFixed(3)}`
                  : ''}
              </Typography>
            </>
          )}
        </Box>

        {/* Expected this hour */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: 0.3 }}>
            Expected This Hour
          </Typography>
          {expected_now ? (
            <>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, display: 'block', textTransform: 'capitalize' }}>
                {expected_now.activity_kind}
                {expected_now.primary_activity && expected_now.primary_activity !== expected_now.activity_kind
                  ? ` · ${expected_now.primary_activity}`
                  : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                {Math.round(expected_now.confidence * 100)}% confidence ·{' '}
                {Math.round(expected_now.presence_probability * 100)}% presence
              </Typography>
            </>
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', display: 'block' }}>
              No forecast slot for hour {new Date(snapshot.snapshot_at).getUTCHours()}
            </Typography>
          )}
        </Box>

        {/* Likely next stops */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: 0.3 }}>
            Likely Next Stops
          </Typography>
          {likely_next_stops.length === 0 ? (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', display: 'block' }}>
              No route patterns match this hour
            </Typography>
          ) : (
            <Stack spacing={0.25} sx={{ mt: 0.25 }}>
              {likely_next_stops.map((s, i) => (
                <Stack key={`${s.cluster}_${i}`} direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.location_hint || s.cluster}
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.6rem' }}>
                    {Math.round(s.probability * 100)}%
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
};

interface ForecastTimelineProps {
  slots: ExpectedDaySlot[];
  onSlotClick?: (slot: ExpectedDaySlot) => void;
}

const ForecastTimeline: React.FC<ForecastTimelineProps> = ({ slots, onSlotClick }) => (
  <Box>
    {/* Colored hour cells */}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', gap: '2px' }}>
      {slots.map((slot) => {
        const baseColor = ACTIVITY_KIND_COLOR[slot.activity_kind] || ACTIVITY_KIND_COLOR.idle;
        const intensity = slot.activity_kind === 'idle' ? 0.3 : 0.4 + slot.confidence * 0.6;
        return (
          <Tooltip
            key={slot.hour}
            title={
              <Box sx={{ fontSize: 11 }}>
                <Box sx={{ fontWeight: 700, mb: 0.5 }}>
                  {String(slot.hour).padStart(2, '0')}:00 — {slot.primary_activity || slot.activity_kind}
                </Box>
                <Box>Confidence: {formatPercent(slot.confidence)}</Box>
                <Box>Presence: {formatPercent(slot.presence_probability)}</Box>
                {slot.visit_pattern_ids.length > 0 && (
                  <Box>Visits: {slot.visit_pattern_ids.length}</Box>
                )}
                {slot.transit_route_ids.length > 0 && (
                  <Box>Transits: {slot.transit_route_ids.length}</Box>
                )}
                {slot.digital_categories.length > 0 && (
                  <Box>Digital: {slot.digital_categories.join(', ')}</Box>
                )}
                {slot.copresence_count > 0 && <Box>Co-presence: {slot.copresence_count}</Box>}
              </Box>
            }
          >
            <Box
              onClick={() => onSlotClick?.(slot)}
              sx={{
                height: 28,
                borderRadius: 0.25,
                bgcolor: baseColor,
                opacity: intensity,
                cursor: onSlotClick ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                transition: 'opacity 120ms ease',
                '&:hover': { opacity: Math.min(1, intensity + 0.15) },
              }}
            >
              <Typography
                sx={{
                  fontSize: 8,
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 600,
                  lineHeight: 1,
                  mb: 0.25,
                }}
              >
                {slot.hour % 3 === 0 ? String(slot.hour).padStart(2, '0') : ''}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
    {/* Hour tick labels — same grid so they align exactly with cells above */}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', gap: '2px', mt: '2px' }}>
      {Array.from({ length: 24 }, (_, h) => (
        <Typography
          key={h}
          sx={{
            fontSize: 8,
            color: 'text.secondary',
            textAlign: 'center',
            lineHeight: 1,
            visibility: h % 3 === 0 ? 'visible' : 'hidden',
          }}
        >
          {String(h).padStart(2, '0')}:00
        </Typography>
      ))}
    </Box>
  </Box>
);
